<template>
  <div :class="'px-2 ' + name">
    <h1 class="text-xs-center" v-if="record && !record.dateRestored">{{ computedTitle }}</h1>
    <v-alert type="success" value="true" v-if="record && record.dateRestored">
      <h1 class="text-xs-center">{{ computedTitle }}</h1>
    </v-alert>

    <div class="layout justify-end mb-3">
      <v-btn color="warning" flat @click="cancel" :disabled="apiInProgress || restoreInProgress">
        Cancel
      </v-btn>

      <v-btn
        v-if="!isNew && (record && !record.dateRestored)"
        color="primary"
        :loading="apiInProgress"
        :disabled="!isEditable"
        @click="saveNotes"
      >
        Save
      </v-btn>

      <v-btn
        v-if="canRestore"
        color="warning"
        :loading="restoreInProgress"
        :disabled="!isEditable"
        @click="restore"
      >
        <v-icon left small>fas fa-undo</v-icon>
        Restore
      </v-btn>

      <v-btn
        v-if="isNew"
        color="primary"
        :loading="apiInProgress"
        :disabled="!isEditable || !canCreate"
        @click="create"
      >
        <v-icon left small>fas fa-plus</v-icon>
        Blacklist
      </v-btn>
    </div>

    <v-layout row wrap class="mb-3">
      <v-flex xs12 md8 offset-md2>
        <div v-if="isNew">
          <v-layout row wrap mb-1>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              Phone number
            </v-flex>
            <v-flex sm8>
              <phone-number-field
                required
                v-model="createForm.phoneNumber"
                :default-prefix="defaultPrefixForPhone"
                :child-props="{ placeholder: 'Enter phone number' }"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-1>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              Confirm phone number
            </v-flex>
            <v-flex sm8>
              <phone-number-field
                required
                v-model="createForm.phoneNumberConfirm"
                :default-prefix="defaultPrefixForConfirm"
                :child-props="{ placeholder: 'Re-enter phone number' }"
              />
              <div v-if="createForm.phoneNumber && createForm.phoneNumberConfirm && createForm.phoneNumber !== createForm.phoneNumberConfirm">
                <small class="error--text">Phone numbers must match</small>
              </div>
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-1>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon small v-on="on">fa fa-info-circle</v-icon>
                </template>
                <div>1. Suspicious: any account registered with this phone number cannot perform refunds, unless an admin permits.</div>
                <div>2. High Risk: any passenger account registered with this phone number cannot use the system and is banned from passenger account usage.</div>
              </v-tooltip>
              Threat level
            </v-flex>
            <v-flex sm8>
              <v-select
                :disabled="!isEditable"
                :items="threatLevelItems"
                label="Select threat level"
                v-model="createForm.threatLevel"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-1>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              Initial note (optional)
            </v-flex>
            <v-flex sm8>
              <v-textarea
                auto-grow
                required
                rows="2"
                v-model="createForm.note"
                :disabled="!isEditable"
                placeholder="Reason / context"
              />
            </v-flex>
          </v-layout>
        </div>

        <div v-if="!isNew && record">
          <v-layout row wrap mb-3>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              Phone number
            </v-flex>
            <v-flex sm8>
              <phone-number-field
                required
                v-model="record.phoneNumber"
                :child-props="{ readonly: true, placeholder: 'Enter phone number' }"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-3>
            <v-flex class="text-xs-right font-weight-bold" sm4 pr-3>
              Threat level
            </v-flex>
            <v-flex sm8>
              <!-- <v-chip small :color="record.threatLevel === 2 ? 'warning' : 'default'">
                {{ record.threatLevel === 2 ? 'High Risk (2)' : 'Suspicious (1)' }}
              </v-chip> -->
              <v-select
                :disabled="!isEditable || record.dateRestored"
                :items="threatLevelItems"
                label="Select threat level"
                v-model="record.threatLevel"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-3>
            <v-flex class="text-xs-right font-weight-bold" sm4 pr-3>
              Date created
            </v-flex>
            <v-flex sm8 align-center>
              {{ record && record.dateCreatedLabel }}
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-3>
            <v-flex class="text-xs-right font-weight-bold" sm4 pr-3>
              Date updated
            </v-flex>
            <v-flex sm8>
              {{ record && record.dateUpdatedLabel }}
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-3>
            <v-flex class="text-xs-right font-weight-bold" sm4 pr-3>
              <v-icon v-if="record && record.dateRestored" small color="success">fas fa-check-circle</v-icon>
              Date restored
            </v-flex>
            <v-flex sm8>
              {{ (record && record.dateRestoredLabel) || '—' }}
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-3>
            <v-flex class="text-xs-right font-weight-bold" sm4 pr-3>
              Blacklisted by user
            </v-flex>
            <v-flex sm8>
              (ID: {{ record && record.byStUserId }}) {{ record && record.byStUserIdLabel }}
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-3 v-if="record && record.restoredByStUserId">
            <v-flex class="text-xs-right font-weight-bold" sm4 pr-3>
              Restored by user
            </v-flex>
            <v-flex sm8>
              (ID: {{ record && record.restoredByStUserId }}) {{ record && record.restoredByStUserIdLabel }}
            </v-flex>
          </v-layout>

          <v-card class="mt-3">
            <v-card-title>
              <strong>Notes ({{ editNotes.length }})</strong>
            </v-card-title>
            <v-card-text>
              <div v-if="!editNotes || !editNotes.length" class="grey--text">No notes yet.</div>
              <div v-else style="max-height: 200px; overflow-y: auto;">
                <v-layout
                  v-for="(n, idx) in editNotes"
                  :key="idx"
                  row
                  wrap
                  mb-2
                >
                  <v-flex sm4 class="text-xs-right font-weight-bold" mt-3 pr-3>
                    <div>{{ (n && n.dateLabel) || '—' }}</div>
                    <div><v-chip small style="font-weight: normal">User ID: {{ (n && n.stUserId) || '—' }}</v-chip></div>
                  </v-flex>
                  <v-flex :sm="n.new ? 7 : 8">
                    <v-textarea
                      auto-grow
                      rows="2"
                      :style="`color: ${!n.new ? '#ccc' : 'inherit'};`"
                      :readonly="!n.new"
                      v-model="editNotes[idx].note"
                      :disabled="!isEditable"
                      placeholder="Note"
                    />
                  </v-flex>
                  <v-flex sm1 v-if="n.new">
                    <v-btn small icon :disabled="!isEditable" @click="deleteNote(idx)">
                      <v-icon small>fa fa-trash</v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
              </div>

              <v-divider class="my-3" />

              <v-layout row wrap mb-1 v-if="isEditable && (!record || !record.dateRestored)">
                <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                  Add note
                </v-flex>
                <v-flex sm8>
                  <v-textarea
                    auto-grow
                    rows="2"
                    v-model="newNoteText"
                    :disabled="!isEditable"
                    placeholder="New note"
                  />
                  <v-btn small :disabled="!isEditable || !newNoteText" @click="addNote">
                    <v-icon left small>fal fa-plus</v-icon>
                    Add
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>

          <v-card class="mt-3">
            <v-card-title>
              <strong>History</strong>
              <v-spacer />
              <v-btn small icon :disabled="historyLoading || !record" :loading="historyLoading" @click="loadHistory" title="Refresh history">
                <v-icon small>fal fa-sync</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="historyHeaders"
                :items="history"
                :loading="historyLoading"
                :rows-per-page-items="[10, 25, 50]"
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <tr>
                    <td>{{ (props.item && props.item.dateCreatedLabel) || '—' }}</td>
                    <td>
                      <span v-if="props.item.dateRestored" class="grey--text">Restored</span>
                      <span v-else class="error--text font-weight-bold">Blacklisted</span>
                    </td>
                    <td>{{ props.item.byStUserId || '—' }}</td>
                    <td>{{ props.item.restoredByStUserId || '—' }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import src from './src'
export default src
</script>
